import { Link } from "react-router-dom";
import { Button, StatusBadge } from "@/design-system/components/ui";
import type { CommunicationLogEntryDto } from "@/api/communications";
import { ChannelIcon } from "./ChannelIcon";
import { DirectionArrow } from "./DirectionArrow";
import { PlacementChip } from "./PlacementChip";
import { relativeTime } from "@/components/ux/relativeTime";
import { cn } from "@/design-system/lib/utils";

interface Props {
  entry: CommunicationLogEntryDto;
  className?: string;
  /** Limit the number of placement chips rendered inline; remainder shows as "+N". */
  maxPlacements?: number;
  /**
   * Renders a Restore action on a DELETED row. Opt-in, so only the surface
   * that can legitimately offer it does — Compliance view. Restore requires
   * Communications.ViewAll, which is the same audience that view is gated on.
   */
  onRestore?: (id: string) => void;
  /** Disables the Restore action while a restore is in flight. */
  restorePending?: boolean;
}

/**
 * Single row in the central inbox, dashboard widget, and entity tab
 * panel. Body snippet truncates at ~120 chars; placement chips
 * truncate to the most-recent N (default 3) with a "+N" overflow chip.
 */
export function CommunicationListRow({
  entry,
  className,
  maxPlacements = 3,
  onRestore,
  restorePending = false,
}: Props) {
  const counterparty =
    entry.participants[0]?.displayName ??
    entry.externalPartyName ??
    entry.externalPhoneNumber ??
    entry.emailToAddress ??
    "Unknown counterparty";

  const snippet = entry.subject ?? entry.body;
  const placements = entry.placements.slice(0, maxPlacements);
  const overflow = entry.placements.length - placements.length;

  // A deleted entry is NOT a link. The detail read excludes deleted entries by
  // design, so linking it would offer a door onto a 404 — the row carries its
  // Restore action instead, and becomes a link again once restored.
  const shell = cn(
    "flex items-start gap-space-3 px-space-3 py-space-3 rounded-md",
    "border border-border-light bg-white-pure",
    !entry.isDeleted &&
      "hover:bg-white-warm transition-colors duration-fast ease-standard",
    className,
  );

  const body = (
    <>
      <div className="flex items-center gap-space-2 shrink-0 w-12 pt-[2px]">
        <ChannelIcon channel={entry.channel} size={18} />
        <DirectionArrow direction={entry.direction} size={14} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-space-2">
          <span className="font-display font-semibold text-small text-onyx truncate">
            {counterparty}
          </span>
          <span className="text-label text-gray-mid shrink-0 tabular-nums">
            {relativeTime(entry.occurredAt)}
          </span>
        </div>
        <p className="text-small text-gray-mid m-0 mt-[2px] line-clamp-1">
          {snippet}
        </p>
        {(entry.placements.length > 0 || entry.isDeleted) && (
          <div className="flex flex-wrap items-center gap-space-1 mt-space-2">
            {placements.map((p) => (
              <PlacementChip key={p.id} placement={p} asStatic />
            ))}
            {overflow > 0 && (
              <span className="inline-flex items-center px-space-2 py-[3px] rounded-md border border-border-light bg-white-warm text-label text-gray-mid">
                +{overflow}
              </span>
            )}
            {entry.isDeleted && <StatusBadge tone="danger">Deleted</StatusBadge>}
            {entry.isDeleted && onRestore && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={restorePending}
                onClick={() => onRestore(entry.id)}
              >
                Restore
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );

  if (entry.isDeleted) {
    return <div className={shell}>{body}</div>;
  }

  return (
    <Link to={`/communications/${entry.id}`} className={shell}>
      {body}
    </Link>
  );
}
